import React, { useEffect, useState } from "react";
import { X, CheckCircle2, XCircle, Calendar, Layers, Flag } from "lucide-react";
import Avatar from "components/ui/Avatar";
import LocalDateTime from "services/date-converter";

const PRIORITY_STYLES = {
  HIGH: "bg-high_bg text-high border-high_border",
  MEDIUM: "bg-warning_bg text-warning border-warning_border",
  LOW: "bg-info_bg text-info border-info_border",
};

const STATUS_STYLES = {
  PENDING: "bg-warning_bg text-warning border-warning_border",
  APPROVED: "bg-success_bg text-success border-success_border",
  REJECTED: "bg-danger_bg text-danger border-danger_border",
};

const STATUS_ACCENT = {
  PENDING: "bg-warning",
  APPROVED: "bg-success",
  REJECTED: "bg-danger",
};

const ApprovalDetail = ({ isOpen, onClose, request, onApprove, onReject, actionLoading }) => {
  const [remarks, setRemarks] = useState("");

  useEffect(() => {
    if (isOpen) setRemarks("");
  }, [isOpen, request?.id]);

  if (!isOpen || !request) return null;

  const isPending = request.status === "PENDING";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]" onClick={onClose} />

      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl border border-border bg-surface shadow-[0_20px_50px_rgba(0,0,0,0.18)]">
        {/* Status accent bar */}
        <div className={`h-1 w-full ${STATUS_ACCENT[request.status] || STATUS_ACCENT.PENDING}`} />

        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h3 className="text-[15px] font-semibold text-text_primary">Request Details</h3>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-text_muted hover:bg-surface_alt hover:text-text_primary"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-5 px-6 py-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[15px] font-semibold text-text_primary">{request.title}</div>
              <div className="mt-1 text-xs text-text_muted">{request.type_label || request.type}</div>
            </div>
            <span
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide ${
                STATUS_STYLES[request.status] || STATUS_STYLES.PENDING
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${STATUS_ACCENT[request.status] || STATUS_ACCENT.PENDING}`} />
              {request.status}
            </span>
          </div>

          {request.description && (
            <p className="text-sm leading-relaxed text-text_secondary">{request.description}</p>
          )}

          {/* Requester */}
          <div className="flex items-center gap-3 rounded-lg border border-border bg-surface_alt p-3">
            <div className="flex-shrink-0">
              <Avatar user={request.requested_by} size={38} />
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-text_primary">
                {request.requested_by?.first_name} {request.requested_by?.last_name}
              </div>
              <div className="truncate text-xs text-text_muted">{request.requested_by?.email}</div>
            </div>
          </div>

          {/* Meta grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-border p-3">
              <div className="flex items-center gap-1.5 text-xs text-text_muted">
                <Layers size={12} /> Module
              </div>
              <div className="mt-1 text-sm font-medium text-text_primary">{request.module || "—"}</div>
            </div>
            <div className="rounded-lg border border-border p-3">
              <div className="flex items-center gap-1.5 text-xs text-text_muted">
                <Flag size={12} /> Priority
              </div>
              <span
                className={`mt-1 inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${
                  PRIORITY_STYLES[request.priority] || PRIORITY_STYLES.LOW
                }`}
              >
                {request.priority || "LOW"}
              </span>
            </div>
            <div className="rounded-lg border border-border p-3">
              <div className="flex items-center gap-1.5 text-xs text-text_muted">
                <Calendar size={12} /> Requested On
              </div>
              <div className="mt-1 text-sm font-medium text-text_primary">
                <LocalDateTime utcTime={request.requested_date} type="datetime" />
              </div>
            </div>
            <div className="rounded-lg border border-border p-3">
              <div className="flex items-center gap-1.5 text-xs text-text_muted">
                <Calendar size={12} /> Due Date
              </div>
              <div className="mt-1 text-sm font-medium text-text_primary">
                {request.due_date ? <LocalDateTime utcTime={request.due_date} type="datetime" /> : "—"}
              </div>
            </div>
          </div>

          {/* Decision info (already actioned) */}
          {!isPending && (
            <div className="rounded-lg border border-border bg-surface_alt p-3 text-xs text-text_secondary">
              <div>
                {request.status === "APPROVED" ? "Approved" : "Rejected"} by{" "}
                <span className="font-medium text-text_primary">
                  {request.reviewed_by?.first_name} {request.reviewed_by?.last_name}
                </span>
              </div>
              {request.reviewed_date && (
                <div className="mt-1">
                  <LocalDateTime utcTime={request.reviewed_date} type="datetime" />
                </div>
              )}
              {request.remarks && <div className="mt-2 italic">"{request.remarks}"</div>}
            </div>
          )}

          {/* Remarks input for pending requests */}
          {isPending && (
            <div>
              <label className="mb-1.5 block text-xs font-medium text-text_secondary">
                Remarks (optional)
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={3}
                placeholder="Add a note for this decision..."
                className="w-full resize-none rounded-md border border-border bg-transparent px-3 py-2 text-sm text-text_primary placeholder:text-text_muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        {isPending && (
          <div className="flex gap-2.5 border-t border-border bg-surface_alt px-6 py-4">
            <button
              disabled={actionLoading}
              onClick={() => onReject(request, remarks)}
              className="flex flex-1 items-center justify-center gap-2 rounded-[10px] border border-danger_border bg-danger_bg px-4 py-2.5 text-[13px] font-medium text-danger disabled:opacity-50"
            >
              <XCircle size={15} /> Reject
            </button>
            <button
              disabled={actionLoading}
              onClick={() => onApprove(request, remarks)}
              className="flex flex-1 items-center justify-center gap-2 rounded-[10px] bg-success px-4 py-2.5 text-[13px] font-medium text-white disabled:opacity-50"
            >
              <CheckCircle2 size={15} /> Approve
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalDetail;
